import { useEffect, useState } from 'react';
import Image from 'components/shared/image';
import Button from 'components/shared/button';
import classNames from 'classnames';
import RichText from 'components/shared/richtext';
import useIsMobile from 'hooks/utils/useIsMobile';
import Script from 'next/script';
import Icon from 'components/shared/icon';
import { useAddItemsToCart, useUpdateItemQuantity, useCart } from 'hooks';
import { getShopifyGid } from 'lib/shopify/shopifyGid';
import styles from './quiz.module.scss';

const Quiz = ({ className, style, data, firstElement = false, locale, fromCollection }) => {
  const {
    quizId,
    quizType,
    openQuizOnLoad,
    headline,
    textStyle,
    textPosition,
    textPositionMobile,
    textColor,
    body,
    media,
    button,
    subtitle,
    resources,
    quizHeightMobile,
    quizHeightDesktop,
    quizWidthMobile,
    quizWidthDesktop,
  } = data;

  const {
    freeItem,
    amountOfProductsToGetFreeItem,
    promoCollectionName,
    shopifyTagsToExcludeFromPromo,
  } = resources.secondpartsettings;

  const [hasItalic, setHasItalic] = useState();
  const [loadEmbedQuiz, setLoadEmbedQuiz] = useState(openQuizOnLoad);
  const [hideEmbedQuiz, setHideEmbedQuiz] = useState(!openQuizOnLoad);
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();
  const addItems = useAddItemsToCart();
  const updateItemQuantity = useUpdateItemQuantity();
  const { cart } = useCart();

  useEffect(() => {
    setHasItalic(textStyle === 'Italic');
  }, [textStyle]);

  const removeScript = () => {
    const existingScript = document.querySelector(
      'script[src="https://admin.revenuehunt.com/embed.js"]'
    );
    if (existingScript) {
      existingScript.remove();
    }
    delete window.prqAppLoaded;
  };

  useEffect(
    () => () => {
      // cleanup
      removeScript();
    },
    []
  );

  useEffect(() => {
    if (loadEmbedQuiz && quizId) {
      removeScript(); // remove script if it already exists, just in case
      const script = document.createElement('script');
      script.src = 'https://admin.revenuehunt.com/embed.js';
      script.async = true;
      document.head.appendChild(script);
    }
  }, [loadEmbedQuiz, quizId]);

  const flexPosition = () => {
    const position = isMobile ? textPositionMobile : textPosition;
    switch (position) {
      case 'Top':
        return 'flex-start';
      case 'Bottom':
        return 'flex-end';
      default:
        return 'center';
    }
  };

  const openQuiz = () => {
    if (!loadEmbedQuiz) setLoadEmbedQuiz(true);
    setHideEmbedQuiz(false);
  };

  const getLineItemById = variantId =>
    cart?.lines?.edges?.find(
      lineItem => lineItem.node.merchandise.id === getShopifyGid('ProductVariant', variantId)
    )?.node;

  const getNewQuantity = (variantId, difference) => {
    const item = getLineItemById(variantId);
    const currentQuantity = item?.quantity || 0;

    let newQuantity = currentQuantity + difference;

    if (newQuantity < 0) newQuantity = 0;

    return newQuantity;
  };

  const manageQuizCartEvent = async () => {
    const pseudoButton = document.querySelector('#quizCartPseudoButton');
    const dataEvent = pseudoButton.getAttribute('data-event');
    const quizData = JSON.parse(dataEvent);
    const { action, variantId } = quizData || {};
    setLoading(true);

    const variants = variantId.toString().split('||');
    if (action === 'add_one' || action === 'add_all') {
      const items = variants.map(variant => ({
        variantId: getShopifyGid('ProductVariant', variant),
        quantity: 1,
      }));
      await addItems(items, freeItem?.product?.shopifyData, {
        amountOfProductsToGetFreeItem,
        promoCollectionName,
        shopifyTagsToExcludeFromPromo,
      });
    }

    if (action === 'remove_one') {
      const items = variants.map(variant => ({
        id: getLineItemById(variant)?.id,
        variantId: getShopifyGid('ProductVariant', variant),
        quantity: getNewQuantity(variant, -1),
      }));
      await updateItemQuantity(items, freeItem?.product?.shopifyData, {
        amountOfProductsToGetFreeItem,
        promoCollectionName,
        shopifyTagsToExcludeFromPromo,
      });
    }

    setLoading(false);
    pseudoButton.removeAttribute('data-event'); // temporary fix to avoid repeated events from quiz app
  };

  const cta = () => {
    if (fromCollection)
      return (
        <div className={styles.buttonContaner}>
          <Button
            locale={locale}
            button={button}
            className={styles.quizSingleButton}
            onClick={openQuiz}
          />
        </div>
      );
    return (
      <div className={styles.ctaContainer}>
        <Image
          src={media.image}
          layout="fill"
          className={styles.media}
          loading={firstElement ? 'eager' : 'lazy'}
          priority={firstElement}
        />
        <div
          className={classNames(styles.flex, {
            [styles.pointerEventsNone]: media?.image?.link,
          })}
          style={{
            justifyContent: flexPosition(),
          }}
        >
          {subtitle && (
            <span className={styles.subtitle} style={{ color: textColor || 'white' }}>
              {subtitle}
            </span>
          )}
          <h2
            className={classNames(styles.headline, { [styles.italic]: hasItalic })}
            style={{ color: textColor || 'white' }}
          >
            {headline}
          </h2>
          {body && <RichText className={styles.body} text={body.json} />}
          {button && (
            <Button
              locale={locale}
              button={button}
              className={styles.quizButton}
              onClick={openQuiz}
            />
          )}
        </div>
      </div>
    );
  };

  const embedQuiz = () => (
    <>
      {quizType === 'Pop-up' && !hideEmbedQuiz && <div className={styles.overlay} />}
      <div
        className={classNames(styles.embedQuizContainer, {
          [styles.hidden]: hideEmbedQuiz,
          [styles.quizPopup]: quizType === 'Pop-up',
          [styles.quizInline]: quizType === 'Inline',
        })}
      >
        {loading && (
          <div className={styles.loading}>
            <Icon type="loading" className={styles.loader} />
          </div>
        )}

        <div
          className={classNames('rh-widget', 'rh-inline', styles.embedQuiz)}
          data-url={`https://admin.revenuehunt.com/public/quiz/${quizId}`}
          data-autoscroll="false"
        />
        {(!openQuizOnLoad || quizType === 'Pop-up') && (
          <button
            type="button"
            className={styles.closeBtn}
            onClick={() => {
              setHideEmbedQuiz(true);
            }}
          >
            <Icon type="close" />
          </button>
        )}
        <button
          type="button"
          id="quizCartPseudoButton"
          style={{ display: 'none' }}
          data-product=""
          onClick={manageQuizCartEvent}
        >
          a
        </button>
        <Script type="text/javascript" id="quizCartPseudoFunctions">{`
          function registerEvent(event) {
            const button = document.querySelector('#quizCartPseudoButton');
            if(button.getAttribute('data-event') !== null) return; // temporary fix to avoid repeated events from quiz app
            button.setAttribute('data-event', JSON.stringify(event));
            button.click();
          }

          function prqAddOneToCartCallback(event){
            registerEvent(event);
          }

          function prqRemoveOneFromCartCallback(event){
            registerEvent(event);
          }

          function prqAddAllToCartCallback(event){
            registerEvent(event);
          }
        `}</Script>
      </div>
    </>
  );

  return (
    <section
      id="sectionQuiz"
      className={classNames(className, styles.section)}
      style={{
        ...style,
        '--quizHeightMobile': quizHeightMobile ? `${quizHeightMobile}rem` : null,
        '--quizHeightDesktop': quizHeightDesktop ? `${quizHeightDesktop}rem` : null,
        '--quizWidthMobile': quizWidthMobile ? `${quizWidthMobile}rem` : null,
        '--quizWidthDesktop': quizWidthDesktop ? `${quizWidthDesktop}rem` : null,
      }}
    >
      {headline && openQuizOnLoad && quizType === 'Inline' && (
        <h2
          className={classNames(styles.headline, styles.topTitle, {
            [styles.italic]: hasItalic,
          })}
          style={{ color: textColor }}
        >
          {headline}
        </h2>
      )}
      {(hideEmbedQuiz || quizType === 'Pop-up') && cta()}
      {loadEmbedQuiz && embedQuiz()}
    </section>
  );
};

export default Quiz;
